import type { FirmContactView } from "../../../../interfaces/models/IFirmContact";
import type { IOfficeNew } from "../../../../interfaces/models/IOffice";
import {
  getFirmContactByFirmId,
  saveFirmContact,
} from "../../../../Services/FirmContact";
import {
  serializeFirmContactPayload,
  toFirmContactPayload,
} from "../../../../utils/firmContactUtils";
import { mergeFirmContactsWithOffices } from "../../../../pages/ManageFirm/Professionals/manageDataAdminOfficeRules";
import {
  applyFirmContactAssignmentsToBlock,
  hasFirmContactAssignmentChanges,
  type FirmContactAssignmentsMap,
} from "./firmContactAssignmentLogic";

export async function syncFirmContactsForPersonAssignments(params: {
  firmId: string;
  firmName: string;
  membershipType?: string;
  offices: IOfficeNew[];
  firmContactData: FirmContactView[];
  assignments: FirmContactAssignmentsMap;
  personId: string;
  personFullName: string;
  personEmail: string;
}): Promise<FirmContactView[] | null> {
  const firmId = String(params.firmId ?? "").trim();
  const personId = String(params.personId ?? "").trim();
  if (!firmId || !personId) {
    return null;
  }
  if (!hasFirmContactAssignmentChanges(params.assignments)) {
    return null;
  }

  const merged = mergeFirmContactsWithOffices(
    params.firmContactData,
    params.offices,
    firmId,
    params.firmName,
    params.membershipType,
  );

  const originalPersistedIds = new Map(
    params.firmContactData
      .filter((contact) => String(contact.id ?? "").trim())
      .map((contact) => [
        String(contact.officeId ?? "").trim(),
        String(contact.id).trim(),
      ]),
  );

  const originalPayloads = new Map(
    merged.map((contact) => [
      String(contact.officeId ?? "").trim(),
      serializeFirmContactPayload(
        toFirmContactPayload(
          contact,
          originalPersistedIds.get(String(contact.officeId ?? "").trim()),
        ),
      ),
    ]),
  );

  const updatedContacts = merged.map((contact) =>
    applyFirmContactAssignmentsToBlock(contact, params.assignments, {
      personId,
      personFullName: params.personFullName,
      personEmail: params.personEmail,
    }),
  );

  const payloads = updatedContacts.map((contact) =>
    toFirmContactPayload(
      contact,
      originalPersistedIds.get(String(contact.officeId ?? "").trim()),
    ),
  );

  const changedPayloads = payloads.filter((payload) => {
    const key = String(payload.officeId ?? "").trim();
    const previous = originalPayloads.get(key);
    return serializeFirmContactPayload(payload) !== previous;
  });

  if (changedPayloads.length === 0) {
    return null;
  }

  for (const payload of changedPayloads) {
    await saveFirmContact(payload);
  }

  const refreshed = await getFirmContactByFirmId(firmId);
  return Array.isArray(refreshed) ? refreshed : [];
}
